<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">{{ editing ? 'Editar gasto' : 'Nuevo gasto' }}</h1>
    </div>

    <form class="custom-form" @submit.prevent="save" novalidate>
      <b-card tag="article" no-body style="max-width: 860px" class="custom-card">
        <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
          <h3>Datos del gasto</h3>
        </b-card-header>
        <b-card-body class="custom-card-body p-0">
          <section class="form-section">
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label>Vehículo *</label>
                  <select v-model="form.vehicle_id" class="custom-form-control"
                    :class="{ 'custom-form-control-error': $v.form.vehicle_id.$error }">
                    <option :value="null" disabled>Seleccionar vehículo</option>
                    <option v-for="v in vehicles" :key="v.id" :value="v.id">{{ v.license_plate }}</option>
                  </select>
                  <FormError v-if="$v.form.vehicle_id.$error && !$v.form.vehicle_id.required"
                    message="El vehículo es requerido" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Fecha *</label>
                  <input type="date" v-model="form.expense_date" class="custom-form-control"
                    :class="{ 'custom-form-control-error': $v.form.expense_date.$error }" />
                  <FormError v-if="$v.form.expense_date.$error && !$v.form.expense_date.required"
                    message="La fecha es requerida" />
                </div>
              </div>

              <div class="col-md-6">
                <div class="form-group">
                  <label>Tipo *</label>
                  <select v-model="form.expense_type" class="custom-form-control"
                    :class="{ 'custom-form-control-error': $v.form.expense_type.$error }">
                    <option :value="null" disabled>Seleccionar tipo</option>
                    <option v-for="t in expenseTypes" :key="t.key" :value="t.key">{{ t.label }}</option>
                  </select>
                  <FormError v-if="$v.form.expense_type.$error && !$v.form.expense_type.required"
                    message="El tipo es requerido" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Método de pago *</label>
                  <select v-model="form.payment_method" class="custom-form-control"
                    :class="{ 'custom-form-control-error': $v.form.payment_method.$error }">
                    <option :value="null" disabled>Seleccionar método</option>
                    <option v-for="m in paymentMethods" :key="m.key" :value="m.key">{{ m.label }}</option>
                  </select>
                  <FormError v-if="$v.form.payment_method.$error && !$v.form.payment_method.required"
                    message="El método de pago es requerido" />
                </div>
              </div>

              <div class="col-md-6">
                <div class="form-group">
                  <label>Monto (S/) *</label>
                  <input type="number" step="0.01" min="0" v-model.number="form.amount"
                    class="custom-form-control" placeholder="0.00"
                    :class="{ 'custom-form-control-error': $v.form.amount.$error }" />
                  <FormError v-if="$v.form.amount.$error && !$v.form.amount.required"
                    message="El monto es requerido" />
                  <FormError v-if="$v.form.amount.$error && $v.form.amount.required && !$v.form.amount.minValue"
                    message="El monto debe ser mayor a 0" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Conductor (opcional)</label>
                  <select v-model="form.driver_id" class="custom-form-control">
                    <option :value="null">Sin conductor</option>
                    <option v-for="d in drivers" :key="d.id" :value="d.id">{{ d.full_name }}</option>
                  </select>
                </div>
              </div>

              <!-- Campos de combustible -->
              <template v-if="form.expense_type === 'FUEL'">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Litros *</label>
                    <input type="number" step="0.01" min="0" v-model.number="form.liters"
                      class="custom-form-control" placeholder="0.00"
                      :class="{ 'custom-form-control-error': $v.form.liters.$error }" />
                    <FormError v-if="$v.form.liters.$error && !$v.form.liters.required"
                      message="Los litros son requeridos para combustible" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Odómetro (km, opcional)</label>
                    <input type="number" step="1" min="0" v-model.number="form.odometer_km"
                      class="custom-form-control" placeholder="Ej: 25290" />
                  </div>
                </div>
              </template>

              <div class="col-md-12">
                <div class="form-group mb-0">
                  <label>Descripción</label>
                  <textarea v-model="form.description" class="custom-form-control" rows="2"
                    placeholder="Detalle del gasto"></textarea>
                </div>
              </div>
            </div>
          </section>

          <!-- ═══════════ FOTOS ═══════════ -->
          <section class="form-section">
            <h2 class="form-subtitle">Fotos</h2>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label>Factura</label>
                  <input type="file" accept="image/*" class="custom-form-control" @change="onFile('invoice', $event)" />
                  <small v-if="existing.has_invoice" class="d-block mt-1">
                    <button type="button" class="photo-link" @click="viewPhoto('invoice')">Ver factura actual</button>
                  </small>
                </div>
              </div>
              <div class="col-md-6" v-if="form.payment_method === 'CASH'">
                <div class="form-group">
                  <label>Comprobante de reembolso (Yape/Plin)</label>
                  <input type="file" accept="image/*" class="custom-form-control" @change="onFile('reimbursement', $event)" />
                  <small v-if="existing.has_reimbursement_proof" class="d-block mt-1">
                    <button type="button" class="photo-link" @click="viewPhoto('reimbursement')">Ver comprobante actual</button>
                  </small>
                </div>
              </div>
            </div>
            <p class="text-muted small mb-0">Imágenes hasta 5 MB. La factura es necesaria para aprobar el gasto.</p>
          </section>

          <section class="form-section">
            <div class="form-group-buttons text-right">
              <router-link :to="{ name: 'fleet-expenses' }" class="button button-light">
                {{ editing ? 'Volver' : 'Cancelar' }}
              </router-link>
              <button class="button button-primary" type="submit">Guardar</button>
            </div>
          </section>
        </b-card-body>
      </b-card>
    </form>

    <!-- Lightbox foto actual -->
    <b-modal v-model="photoModal.show" size="lg" hide-footer :title="photoModal.title">
      <div class="photo-frame text-center">
        <img v-if="photoModal.url" :src="photoModal.url" alt="Foto" class="photo-img" />
        <p v-else-if="photoModal.error" class="text-muted m-0 py-4">{{ photoModal.error }}</p>
        <p v-else class="text-muted m-0 py-4">Cargando imagen…</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert, FormError } from 'wize-admin'
import { required, requiredIf, minValue } from 'vuelidate/lib/validators'
import { FleetExpenseService, VehicleService, DriverService } from '@/core/services'

const EXPENSE_TYPES = [
  { key: 'FUEL', label: 'Combustible' },
  { key: 'PARKING', label: 'Estacionamiento' },
  { key: 'OTHER', label: 'Otros' }
]
const PAYMENT_METHODS = [
  { key: 'CASH', label: 'Efectivo' },
  { key: 'CARD', label: 'Tarjeta' },
  { key: 'TRANSFER', label: 'Transferencia' },
  { key: 'OTHER', label: 'Otro' }
]
const MAX_PHOTO_BYTES = 5 * 1024 * 1024

export default {
  name: 'ExpenseEditor',
  components: { FormError },
  data() {
    return {
      editing: false,
      vehicles: [],
      drivers: [],
      expenseTypes: EXPENSE_TYPES,
      paymentMethods: PAYMENT_METHODS,
      form: {
        vehicle_id: null,
        expense_date: null,
        expense_type: null,
        payment_method: null,
        amount: null,
        driver_id: null,
        liters: null,
        odometer_km: null,
        description: null
      },
      files: { invoice: null, reimbursement: null },
      existing: { has_invoice: false, has_reimbursement_proof: false },
      photoModal: { show: false, title: '', url: null, error: null }
    }
  },
  validations: {
    form: {
      vehicle_id: { required },
      expense_date: { required },
      expense_type: { required },
      payment_method: { required },
      amount: { required, minValue: minValue(0.01) },
      liters: {
        required: requiredIf(function () {
          return this.form.expense_type === 'FUEL'
        })
      }
    }
  },
  watch: {
    // Si deja de ser CASH, se descarta el comprobante seleccionado.
    'form.payment_method': function (v) {
      if (v !== 'CASH') this.files.reimbursement = null
    }
  },
  methods: {
    onFile(kind, event) {
      const file = event.target.files && event.target.files[0]
      if (!file) { this.files[kind] = null; return }
      if (!file.type.startsWith('image/')) {
        Alert.error('El archivo debe ser una imagen')
        event.target.value = ''
        return
      }
      if (file.size > MAX_PHOTO_BYTES) {
        Alert.error('La imagen supera el máximo de 5 MB')
        event.target.value = ''
        return
      }
      this.files[kind] = file
    },
    buildPayload() {
      const f = this.form
      return {
        vehicle_id: f.vehicle_id,
        expense_date: f.expense_date,
        expense_type: f.expense_type,
        payment_method: f.payment_method,
        amount: Number(f.amount),
        driver_id: f.driver_id || null,
        description: f.description || null,
        liters: f.expense_type === 'FUEL' && f.liters != null ? Number(f.liters) : null,
        odometer_km: f.expense_type === 'FUEL' && f.odometer_km != null && f.odometer_km !== ''
          ? Number(f.odometer_km) : null
      }
    },
    async uploadPending(id) {
      // Sube las fotos seleccionadas (secuencial). La factura primero.
      if (this.files.invoice) {
        await FleetExpenseService.uploadPhoto(id, 'invoice', this.files.invoice)
      }
      if (this.files.reimbursement && this.form.payment_method === 'CASH') {
        await FleetExpenseService.uploadPhoto(id, 'reimbursement', this.files.reimbursement)
      }
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({ scrollTo: document.querySelector('.form-error'), updateHistory: false, offset: -50 })
        })
        return
      }
      try {
        await this.$store.dispatch('app/loading', true)
        const payload = this.buildPayload()

        if (!this.editing) {
          // Crear (JSON) → obtener id → subir fotos → volver a la lista.
          const res = await FleetExpenseService.create(payload)
          const id = res.payload.id
          await this.uploadPending(id)
          Alert.success('Gasto registrado correctamente')
          this.$router.push({ name: 'fleet-expenses' })
        } else {
          const id = this.$route.params.id
          await FleetExpenseService.update(id, payload)
          await this.uploadPending(id)
          this.files = { invoice: null, reimbursement: null }
          Alert.success('Gasto actualizado correctamente')
          await this.load(id)
        }
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo guardar el gasto')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async load(id) {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await FleetExpenseService.get(id)
        const p = res.payload
        this.form = {
          vehicle_id: p.vehicle_id,
          expense_date: p.expense_date ? p.expense_date.slice(0, 10) : null,
          expense_type: p.expense_type,
          payment_method: p.payment_method,
          amount: p.amount,
          driver_id: p.driver_id || null,
          liters: p.liters,
          odometer_km: p.odometer_km,
          description: p.description
        }
        this.existing = { has_invoice: p.has_invoice, has_reimbursement_proof: p.has_reimbursement_proof }
        this.editing = true
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo cargar el gasto')
        this.$router.push({ name: 'fleet-expenses' })
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async viewPhoto(kind) {
      this.photoModal = {
        show: true,
        title: kind === 'invoice' ? 'Factura' : 'Comprobante de reembolso',
        url: null, error: null
      }
      try {
        const res = await FleetExpenseService.getPhoto(this.$route.params.id, kind)
        this.photoModal.url = res.payload ? res.payload.url : null
        if (!this.photoModal.url) this.photoModal.error = 'No hay foto disponible.'
      } catch (e) {
        console.error(e)
        this.photoModal.error = e.errors ? e.errors.message : 'No se pudo cargar la foto.'
      }
    },
    async loadVehicles() {
      try {
        const res = await VehicleService.list({ is_travelcab: true }, { page: 0, size: 1000 })
        const payload = res.payload || {}
        this.vehicles = payload.items || payload || []
      } catch (e) { console.error(e) }
    },
    async loadDrivers() {
      try {
        const res = await DriverService.list({}, { page: 0, size: 1000 })
        const payload = res.payload || {}
        this.drivers = payload.items || payload || []
      } catch (e) { console.error(e) }
    }
  },
  async created() {
    await this.loadVehicles()
    await this.loadDrivers()
    if (this.$route.params.id) await this.load(this.$route.params.id)
  }
}
</script>

<style lang="stylus" scoped>
.photo-link
  background none
  border none
  padding 0
  color #007bff
  cursor pointer
  text-decoration underline
.photo-frame
  background #f4f5f7
  border 1px solid #e5e7eb
  border-radius 8px
  min-height 220px
  display flex
  align-items center
  justify-content center
  overflow hidden
.photo-img
  max-width 100%
  max-height 70vh
  object-fit contain
</style>
